import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Download, Mail, Phone, Filter, Users, ChevronDown, Loader2, Trash2, Edit, Check, X } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedLead, setExpandedLead] = useState(null);
  const [updatingNote, setUpdatingNote] = useState(null);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Edit state
  const [editingLead, setEditingLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    source: 'all',
    dateFrom: '',
    dateTo: ''
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    nuovo: 0,
    contattato: 0,
    qualificato: 0,
    convertito: 0
  });

  useEffect(() => {
    fetchLeads();
  }, [filters]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      let params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.source !== 'all') params.append('source', filters.source);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const response = await api.get(`/admin/leads?${params.toString()}`);
      setLeads(response.data);
      
      // Calc stats
      const allLeads = response.data;
      setStats({
        total: allLeads.length,
        nuovo: allLeads.filter(l => l.status === 'nuovo').length,
        contattato: allLeads.filter(l => l.status === 'contattato').length,
        qualificato: allLeads.filter(l => l.status === 'qualificato' || l.status === 'in_trattativa').length,
        convertito: allLeads.filter(l => l.status === 'convertito' || l.status === 'chiuso').length
      });
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // ============= STATUS & CATEGORY UPDATE =============
  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.put(`/admin/leads/${leadId}/status?status=${status}`);
      toast.success('Stato aggiornato');
      fetchLeads();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const updateLeadCategory = async (leadId, category) => {
    try {
      await api.put(`/admin/leads/${leadId}/category?category=${encodeURIComponent(category)}`);
      toast.success('Categoria aggiornata');
      fetchLeads();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const updateLeadNotes = async (leadId, notes) => {
    setUpdatingNote(leadId);
    try {
      await api.put(`/admin/leads/${leadId}/notes`, { notes });
      toast.success('Note salvate');
      fetchLeads();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setUpdatingNote(null);
    }
  };

  // ============= FULL EDIT =============
  const openEditModal = (lead) => {
    setEditingLead(lead);
    setEditForm({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      category: lead.category || 'Da qualificare',
      status: lead.status || 'nuovo',
      notes: lead.notes || ''
    });
  };

  const saveLeadEdit = async () => {
    if (!editingLead) return;
    setSaving(true);
    try {
      await api.put(`/admin/leads/${editingLead.id}/full`, editForm);
      toast.success('Lead aggiornato');
      setEditingLead(null);
      fetchLeads();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ============= DELETE =============
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredLeads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredLeads.map(l => l.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const deleteSingleLead = async (leadId) => {
    if (!confirm('Eliminare questo lead?')) return;
    try {
      await api.delete(`/admin/leads/${leadId}`);
      toast.success('Lead eliminato');
      fetchLeads();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const deleteSelectedLeads = async () => {
    if (selectedIds.length === 0) return;
    setDeleting(true);
    try {
      await api.post('/admin/leads/delete-multiple', { ids: selectedIds });
      toast.success(`${selectedIds.length} lead eliminati`);
      setSelectedIds([]);
      setShowDeleteModal(false);
      fetchLeads();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Errore nell\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  // ============= EXPORT =============
  const exportLeads = async () => {
    try {
      const response = await api.get('/admin/leads/export');
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export completato');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore nell\'export');
    }
  };

  // ============= UI HELPERS =============
  const getStatusBadge = (status) => {
    const variants = {
      nuovo: 'bg-blue-100 text-blue-700',
      contattato: 'bg-yellow-100 text-yellow-700',
      in_trattativa: 'bg-purple-100 text-purple-700',
      qualificato: 'bg-purple-100 text-purple-700',
      chiuso: 'bg-green-100 text-green-700',
      convertito: 'bg-green-100 text-green-700',
      perso: 'bg-red-100 text-red-700',
      archiviato: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      nuovo: 'Nuovo',
      contattato: 'Contattato',
      in_trattativa: 'In Trattativa',
      qualificato: 'Qualificato',
      chiuso: 'Chiuso',
      convertito: 'Convertito',
      perso: 'Perso',
      archiviato: 'Archiviato'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${variants[status] || variants.nuovo}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const colors = {
      'Matrimonio': 'bg-pink-100 text-pink-700',
      'Ritratto': 'bg-blue-100 text-blue-700',
      'Evento': 'bg-orange-100 text-orange-700',
      'Corporate': 'bg-indigo-100 text-indigo-700',
      'Azienda': 'bg-indigo-100 text-indigo-700',
      'Da qualificare': 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors[category] || colors['Da qualificare']}`}>
        {category}
      </span>
    );
  };

  const getSourceLabel = (source) => {
    const labels = {
      'contact_form': 'Form Contatto',
      'booking': 'Prenotazione',
      'newsletter': 'Newsletter',
      'review': 'Recensione',
      'payment': 'Pagamento'
    };
    return labels[source] || source;
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(query) ||
      lead.email?.toLowerCase().includes(query) ||
      lead.message?.toLowerCase().includes(query) ||
      lead.phone?.toLowerCase().includes(query)
    );
  });

  const resetFilters = () => {
    setFilters({
      status: 'all',
      category: 'all',
      source: 'all',
      dateFrom: '',
      dateTo: ''
    });
    setSearchQuery('');
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2">CRM - Lead</h1>
          <p className="text-muted-foreground">Gestisci i contatti e le opportunità</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportLeads} data-testid="export-leads-btn">
            <Download size={16} className="mr-2" />
            Esporta CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totali</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Users className="text-muted-foreground" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Nuovi</p>
            <p className="text-2xl font-bold text-blue-700">{stats.nuovo}</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Contattati</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.contattato}</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">In Trattativa</p>
            <p className="text-2xl font-bold text-purple-700">{stats.qualificato}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Chiusi</p>
            <p className="text-2xl font-bold text-green-700">{stats.convertito}</p>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center justify-between">
          <span className="text-sm text-red-700">
            {selectedIds.length} lead selezionati
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedIds([])}>
              Deseleziona
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => setShowDeleteModal(true)}
              data-testid="delete-selected-btn"
            >
              <Trash2 size={14} className="mr-1" />
              Elimina selezionati
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="py-4">
          <div className="flex items-center gap-2">
            <Filter size={18} />
            <CardTitle className="text-base">Filtri</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca per nome, email, telefono..."
                  className="pl-10"
                  data-testid="leads-search"
                />
              </div>
            </div>

            {/* Status */}
            <Select value={filters.status} onValueChange={(v) => setFilters(f => ({...f, status: v}))}>
              <SelectTrigger data-testid="leads-status-filter">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="nuovo">Nuovo</SelectItem>
                <SelectItem value="contattato">Contattato</SelectItem>
                <SelectItem value="in_trattativa">In Trattativa</SelectItem>
                <SelectItem value="chiuso">Chiuso</SelectItem>
                <SelectItem value="perso">Perso</SelectItem>
                <SelectItem value="archiviato">Archiviato</SelectItem>
              </SelectContent>
            </Select>

            {/* Category */}
            <Select value={filters.category} onValueChange={(v) => setFilters(f => ({...f, category: v}))}>
              <SelectTrigger data-testid="leads-category-filter">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                <SelectItem value="Matrimonio">Matrimonio</SelectItem>
                <SelectItem value="Ritratto">Ritratto</SelectItem>
                <SelectItem value="Evento">Evento</SelectItem>
                <SelectItem value="Corporate">Corporate</SelectItem>
                <SelectItem value="Da qualificare">Da qualificare</SelectItem>
              </SelectContent>
            </Select>

            {/* Source */}
            <Select value={filters.source} onValueChange={(v) => setFilters(f => ({...f, source: v}))}>
              <SelectTrigger data-testid="leads-source-filter">
                <SelectValue placeholder="Fonte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le fonti</SelectItem>
                <SelectItem value="contact_form">Form Contatto</SelectItem>
                <SelectItem value="booking">Prenotazione</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="review">Recensione</SelectItem>
              </SelectContent>
            </Select>

            {/* Reset */}
            <Button variant="outline" onClick={resetFilters} className="w-full">
              Reset Filtri
            </Button>
          </div>

          {/* Date Range */}
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Dal:</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(f => ({...f, dateFrom: e.target.value}))}
                className="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Al:</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(f => ({...f, dateTo: e.target.value}))}
                className="w-40"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Select All */}
      {filteredLeads.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <Checkbox
            checked={selectedIds.length === filteredLeads.length && filteredLeads.length > 0}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-leads"
          />
          <span className="text-sm text-muted-foreground">
            Seleziona tutti ({filteredLeads.length})
          </span>
        </div>
      )}

      {/* Leads List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => (
            <motion.div
              key={lead.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-card border rounded-sm overflow-hidden ${selectedIds.includes(lead.id) ? 'border-primary ring-1 ring-primary' : 'border-border'}`}
              data-testid={`lead-item-${lead.id}`}
            >
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* Checkbox + Info */}
                  <div className="flex gap-4 flex-1">
                    <Checkbox
                      checked={selectedIds.includes(lead.id)}
                      onCheckedChange={() => toggleSelect(lead.id)}
                      className="mt-1"
                      data-testid={`select-lead-${lead.id}`}
                    />
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-medium">{lead.name}</h3>
                        {getStatusBadge(lead.status)}
                        {getCategoryBadge(lead.category)}
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          {getSourceLabel(lead.source)}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm mb-2">
                        <a
                          href={`mailto:${lead.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                        >
                          <Mail size={14} />
                          {lead.email}
                        </a>
                        {lead.phone && (
                          <a
                            href={`tel:${lead.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                          >
                            <Phone size={14} />
                            {lead.phone}
                          </a>
                        )}
                      </div>

                      <p className="text-muted-foreground text-sm line-clamp-2">{lead.message}</p>

                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(lead.created_at).toLocaleString('it-IT')}
                        {lead.service_interest && (
                          <span className="ml-2">• Interesse: {lead.service_interest}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(lead)}
                      data-testid={`edit-lead-${lead.id}`}
                    >
                      <Edit size={14} className="mr-1" />
                      Modifica
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteSingleLead(lead.id)}
                      data-testid={`delete-lead-${lead.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                    <ChevronDown 
                      size={20} 
                      className={`text-muted-foreground transition-transform cursor-pointer ${expandedLead === lead.id ? 'rotate-180' : ''}`}
                      onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                    />
                  </div>
                </div>
              </div>

              {/* Expanded Section */}
              {expandedLead === lead.id && (
                <div className="border-t border-border p-6 bg-muted/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Full Message */}
                    <div>
                      <Label className="mb-2 block text-sm font-medium">Messaggio completo</Label>
                      <div className="bg-background p-4 rounded border text-sm whitespace-pre-wrap">
                        {lead.message}
                      </div>
                    </div>

                    {/* Quick Edit */}
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block text-sm font-medium">Stato</Label>
                        <Select
                          value={lead.status}
                          onValueChange={(value) => updateLeadStatus(lead.id, value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nuovo">Nuovo</SelectItem>
                            <SelectItem value="contattato">Contattato</SelectItem>
                            <SelectItem value="in_trattativa">In Trattativa</SelectItem>
                            <SelectItem value="chiuso">Chiuso</SelectItem>
                            <SelectItem value="perso">Perso</SelectItem>
                            <SelectItem value="archiviato">Archiviato</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="mb-2 block text-sm font-medium">Categoria</Label>
                        <Select
                          value={lead.category}
                          onValueChange={(value) => updateLeadCategory(lead.id, value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Matrimonio">Matrimonio</SelectItem>
                            <SelectItem value="Ritratto">Ritratto</SelectItem>
                            <SelectItem value="Evento">Evento</SelectItem>
                            <SelectItem value="Corporate">Corporate</SelectItem>
                            <SelectItem value="Da qualificare">Da qualificare</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="mb-2 block text-sm font-medium">Note interne</Label>
                        <Textarea
                          defaultValue={lead.notes || ''}
                          placeholder="Aggiungi note su questo lead..."
                          rows={3}
                          onBlur={(e) => {
                            if (e.target.value !== (lead.notes || '')) {
                              updateLeadNotes(lead.id, e.target.value);
                            }
                          }}
                        />
                        {updatingNote === lead.id && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Loader2 size={12} className="animate-spin" /> Salvataggio...
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {!loading && filteredLeads.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          Nessun lead trovato con i filtri selezionati
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Stai per eliminare <strong>{selectedIds.length}</strong> lead. Questa azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteSelectedLeads}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />}
              Elimina {selectedIds.length} lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Modal */}
      <Dialog open={!!editingLead} onOpenChange={(open) => !open && setEditingLead(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(f => ({...f, name: e.target.value}))}
                placeholder="Nome completo"
                data-testid="edit-lead-name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(f => ({...f, email: e.target.value}))}
                placeholder="email@esempio.com"
                data-testid="edit-lead-email"
              />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm(f => ({...f, phone: e.target.value}))}
                placeholder="+39 333 1234567"
                data-testid="edit-lead-phone"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm(f => ({...f, category: v}))}>
                  <SelectTrigger data-testid="edit-lead-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Matrimonio">Matrimonio</SelectItem>
                    <SelectItem value="Ritratto">Ritratto</SelectItem>
                    <SelectItem value="Evento">Evento</SelectItem>
                    <SelectItem value="Corporate">Corporate</SelectItem>
                    <SelectItem value="Da qualificare">Da qualificare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="edit-lead-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nuovo">Nuovo</SelectItem>
                    <SelectItem value="contattato">Contattato</SelectItem>
                    <SelectItem value="in_trattativa">In Trattativa</SelectItem>
                    <SelectItem value="chiuso">Chiuso</SelectItem>
                    <SelectItem value="perso">Perso</SelectItem>
                    <SelectItem value="archiviato">Archiviato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Note interne</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(f => ({...f, notes: e.target.value}))}
                placeholder="Note sul lead..."
                rows={3}
                data-testid="edit-lead-notes"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingLead(null)} disabled={saving}>
              <X size={14} className="mr-1" /> Annulla
            </Button>
            <Button onClick={saveLeadEdit} disabled={saving || !editForm.name || !editForm.email} data-testid="save-lead-btn">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
              Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
